import { Fragment } from 'react'
import createStyles from '@mui/styles/createStyles'
import makeStyles from '@mui/styles/makeStyles'
import { Box } from '@mui/material'
import { Attribute, AttributeLabel, AttributeValue } from 'packages/eid-ui'
import { useAccessRequestPolicy } from 'hooks'
import { useTranslation } from 'react-i18next'
import { Skeleton } from '@mui/material'
import DynamicComponents from 'components/DynamicComponents'
import { ResponsibleParty } from 'components'
import useSubcomponents from 'useSubcomponents'
import { ResourceOwnersList } from 'components/Owners/ResourceOwnersList'
import { ResourceApproversList } from 'components/Owners/ResourceApproversList'

const smallScreenWidth = 960
const canSeeOwnersControlName =
    'ResourceAdmin-ManagementRolesDetails-Overview-Owners-Control'
const canSeeApproversControlName =
    'ResourceAdmin-ManagementRolesDetails-Overview-Approvers-Control'
const useStyles = makeStyles((theme) =>
    createStyles({
        root: {
            position: 'relative',
            padding: theme.spacing(2),
            display: 'flex',
            miHeight: '156px',
            justifyContent: 'space-between',

            [`@media (max-width:${smallScreenWidth - 1}px)`]: {
                flexDirection: 'column',
            },
        },
        content: {
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',

            [`@media (max-width:345px)`]: {
                flexDirection: 'column-reverse',
            },
            [`@media (max-width:${smallScreenWidth - 1}px)`]: {
                display: 'block',
            },
        },
    }),
)

export const MainOverview = (props) => {
    const classes = useStyles()
    const { hasAccessToControl } = useSubcomponents()
    const { basicAttributes, detailsLoading, managementRole } = props
    const policy = useAccessRequestPolicy(
        managementRole && managementRole.requestPolicyId,
    )

    const { t } = useTranslation()

    const renderComponent = (a, item) => {
        const DynamicComponentToRender =
            DynamicComponents[a.component.componentName]
        return <DynamicComponentToRender attribute={a} data={{ item }} />
    }

    const managementRoleDetails = (
        <>
            {basicAttributes &&
                basicAttributes.map((att, i) => {
                    return (
                        <Attribute key={att.value + i}>
                            <AttributeLabel>{t(att.label)}</AttributeLabel>
                            <AttributeValue>
                                {att.component &&
                                    renderComponent(att, managementRole)}
                                {managementRole &&
                                    !att.hideValue &&
                                    managementRole[att.value]}
                            </AttributeValue>
                        </Attribute>
                    )
                })}
        </>
    )
    const policyDetails = (
        <>
            <Attribute>
                <AttributeLabel>{t('PublishInIAMShop')}</AttributeLabel>
                <AttributeValue>
                    {managementRole?.requestable ? t('Yes') : t('No')}
                </AttributeValue>
            </Attribute>
            <Attribute>
                <AttributeLabel>{t('AccessRequestPolicy')}</AttributeLabel>
                <AttributeValue>{policy && policy.name}</AttributeValue>
            </Attribute>
            <Attribute>
                <AttributeLabel>
                    {t('AllowsTimeConstrainedAccess')}
                </AttributeLabel>
                <AttributeValue>
                    {policy && policy.isTimeConstrained ? t('Yes') : t('No')}
                </AttributeValue>
            </Attribute>
            <Attribute>
                <AttributeLabel>{t('DefaultAccessDuration')}</AttributeLabel>
                <AttributeValue>
                    {policy && policy.defaultValueInMinutes}
                </AttributeValue>
            </Attribute>
            <Attribute>
                <AttributeLabel>{t('MaxAccessDuration')}</AttributeLabel>
                <AttributeValue>
                    {policy && policy.maximumValueInMinutes}
                </AttributeValue>
            </Attribute>
        </>
    )

    const renderContent = () => (
        <Box className={classes.content}>
            <Box>{managementRoleDetails}</Box>
            <Box>{policyDetails}</Box>
            {(hasAccessToControl(canSeeApproversControlName) ||
                hasAccessToControl(canSeeOwnersControlName)) && (
                <Box>
                    {hasAccessToControl(canSeeOwnersControlName) && (
                        <Box
                            data-protectedsubcomponent={canSeeOwnersControlName}
                        >
                            <ResourceOwnersList
                                resourceId={managementRole?.id}
                                label="ManagementRoles_Owners"
                                contextKey="RequestViewManagementRolesOwners"
                            />
                        </Box>
                    )}
                    {hasAccessToControl(canSeeApproversControlName) && (
                        <Box
                            data-protectedsubcomponent={
                                canSeeApproversControlName
                            }
                        >
                            <ResourceApproversList
                                resourceId={managementRole?.id}
                                label="Approvers"
                                contextKey="RequestViewManagementRolesApprovers"
                            />
                        </Box>
                    )}
                    {managementRole?.ownerIsPerson ? (
                        <ResponsibleParty
                            responsiblePersonId={managementRole?.ownerId}
                        />
                    ) : (
                        <Attribute>
                            <AttributeLabel>
                                {t('ResponsibleParty')}
                            </AttributeLabel>
                            <AttributeValue>
                                {managementRole?.ownerFriendlyName || '-'}
                            </AttributeValue>
                        </Attribute>
                    )}
                </Box>
            )}
        </Box>
    )

    return (
        <>
            {detailsLoading ? (
                <Box width={'100%'}>
                    <Skeleton height={40} />
                </Box>
            ) : (
                <Fragment>
                    <Box>{renderContent()}</Box>
                </Fragment>
            )}
        </>
    )
}
